import { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';
import { createAppTheme } from '../themes/theme';
import HeroSection from './HeroSection';
import WelcomeSection from './WelcomeSection';
import AwesomeTeachers from './AwesomeTeachers';
import FactsAboutUs from './FactsAboutUs';
import AboutUs from './AboutUs';
import FacilitiesCardCarousel from './FacilitiesCardCarousel';
import AnimatedSection from './AnimatedSection';
import TestimonialsSection from './TestimonialsSection';
import StaffMessages from './StaffMessages';
import StudentPerformers from './StudentPerformers';
import SplashScreen from './SplashScreen';
import innerLogo from '../assets/InnerLOGO.svg';
import outerLogo from '../assets/OuterLOGO.svg';
import SchoolCalendar from './SchoolCalendar';

interface AboutContent {
  title: string;
  schoolName: string;
  description: string;
}

export default function Home() {
  const [showSplash, setShowSplash] = useState<boolean>(true);
  const [content] = useState<AboutContent>({
    title: "",
    schoolName: "",
    description: ""
  });

  const theme = createAppTheme(0);

  const handleSplashClose = () => {
    setShowSplash(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {}, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div className="App relative min-h-screen overflow-x-hidden w-full overflow-y-hidden">
        {/* Modern background */}
        <div className="fixed inset-0 z-0 bg-gradient-to-br from-[#f8f9ff] to-[#e6f0ff]"></div>
        <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-blue-100 mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 left-20 w-96 h-96 rounded-full bg-emerald-100 mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 right-1/4 w-72 h-72 rounded-full bg-amber-100 mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Splash Screen */}
        {showSplash && (
          <SplashScreen 
            innerLogoSrc={innerLogo} 
            outerCircleSrc={outerLogo}
            scrollFadeThreshold={100}
            onClose={handleSplashClose}
          />
        )}

        {/* Main Content */}
        <div className="main-content px-0 min-w-screen relative z-10">
          <HeroSection />

          {/* Modern sections with updated animations */}
          <AnimatedSection animation="fadeIn" delay={100} id="welcome-section">
            <WelcomeSection />
          </AnimatedSection>
         
          <AnimatedSection animation="zoomIn" delay={300} id="about-us-section">
            <AboutUs />
          </AnimatedSection>
          
           
          <AnimatedSection animation="slideLeft" delay={200} id="calendar-section">
            <SchoolCalendar />
          </AnimatedSection>
          

          <AnimatedSection animation="staggerFadeIn" delay={400} id="staff-messages">
            <StaffMessages />
          </AnimatedSection>
          
          <AnimatedSection animation="slideUp" delay={500} id="student-performers">
            <StudentPerformers />
          </AnimatedSection>
          
          <AnimatedSection animation="fadeIn" delay={600} id="testimonials-section">
            <TestimonialsSection />
          </AnimatedSection>
          
          <AnimatedSection animation="staggerFadeIn" delay={700} id="awesome-teachers">
            <AwesomeTeachers />
          </AnimatedSection>
          
          <AnimatedSection animation="fadeIn" delay={800} id="facts-section">
            <FactsAboutUs />
          </AnimatedSection>
          
          <AnimatedSection animation="slideRight" delay={900} id="facilities-section">
            <FacilitiesCardCarousel />
          </AnimatedSection>
        </div>
      </div>
    </ThemeProvider>
  );
}